import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: { width: "100%" },
  section: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  runRow: {
    cursor: "pointer",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  runRowSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  secondaryText: { color: theme.palette.text.secondary },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "160px 1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
});

const basePath = utils.getEdgeBasePath();

const labels = {
  title: "ביקורת הזמנות CreditGuard",
  newRun: "הרצת ביקורת חדשה",
  since: "מתאריך",
  until: "עד תאריך",
  limit: "מקסימום הזמנות",
  startRun: "הרץ ביקורת",
  reloadRuns: "רענון רשימת הרצות",
  history: "הרצות אחרונות",
  selectRun: "בחר הרצה מהרשימה או הרץ ביקורת חדשה",
  filters: "סינון תוצאות (הרצה נבחרת)",
  orderStatus: "סטטוס הזמנה מקומי",
  orderStatusBoth: "הכל (paid + closed)",
  orderStatusPaid: "רק paid",
  orderStatusClosed: "רק closed",
  includeTest: "הצג הזמנות בדיקה פנימיות",
  outcomes: "תוצאות",
  outcomeVerified: "מאומת",
  outcomeSuspicious: "חשוד",
  outcomeExcluded: "הוצא (בדיקה)",
  outcomeErrors: "שגיאות ביקורת",
  table: {
    orderId: "מספר הזמנה",
    txId: "מזהה עסקה",
    classification: "סיווג",
    source: "מקור",
    actions: "פעולות",
  },
  detail: "פירוט שורה",
  close: "סגור",
  liveFallbackNotice:
    "אין צילום מצב שנשמר בשורה זו. הנתונים המוצגים נלקחים מהזמנה החיה כעת — אינם משקפים בהכרח את מצב ההזמנה בזמן ההרצה.",
  sourceLabels: {
    cache: "מטמון",
    fresh_inquiry: "שאילתה חיה",
    none: "—",
  },
};

function toDateInputValue(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseDateInput(value, endOfDay) {
  if (!value) return null;
  const d = new Date(value + (endOfDay ? "T23:59:59.999" : "T00:00:00.000"));
  return Number.isNaN(d.getTime()) ? null : d;
}

function defaultSixMonthRange() {
  const until = new Date();
  const since = new Date(until);
  since.setMonth(since.getMonth() - 6);
  return { since, until };
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("he-IL");
}

function formatAgorot(agorot) {
  if (agorot == null || !Number.isFinite(Number(agorot))) return "—";
  return `₪${(Number(agorot) / 100).toFixed(2)}`;
}

const OUTCOME_KEYS = {
  verified: "verified",
  suspicious: "suspicious",
  excluded: "excluded_test_order",
  errors: "audit_error",
};

function classificationChipColor(c) {
  if (c === "verified") return "primary";
  if (c === "suspicious") return "secondary";
  if (c === "excluded_test_order") return "default";
  if (c === "audit_error") return "secondary";
  return "default";
}

/** @param {object} row */
function getResultLocalView(row) {
  if (!row) return {};
  return row.localOrder || row.localOrderFallback || {};
}

/** @param {object} row */
function rowUsesLiveLocalFallback(row) {
  return !!(row && !row.localOrder && row.localOrderFallback);
}

class CreditGuardOrderAuditManager extends React.Component {
  constructor(props) {
    super(props);
    const { since, until } = defaultSixMonthRange();
    this.runDetailRequestSeq = 0;
    this.state = {
      runs: [],
      runsLoading: false,
      runsPage: 0,
      runsRowsPerPage: 10,
      runsTotal: 0,
      selectedRunId: null,
      runDetail: null,
      resultsTotal: 0,
      detailLoading: false,
      sinceInput: toDateInputValue(since),
      untilInput: toDateInputValue(until),
      limitInput: "500",
      runSubmitting: false,
      orderStatusFilter: "both",
      includeTestOrders: false,
      outcomeFilters: {
        verified: true,
        suspicious: true,
        excluded_test_order: true,
        audit_error: true,
      },
      tablePage: 0,
      tableRowsPerPage: 25,
      detailOpen: false,
      detailResult: null,
      snackbar: { open: false, message: "", severity: "success" },
    };
  }

  componentDidMount() {
    this.loadRuns();
  }

  getAdminHeaders = (json = false) => {
    const token =
      typeof window !== "undefined"
        ? window.localStorage.getItem("admin_token")
        : "";
    const headers = token ? { "x-auth-token": token } : {};
    if (json) headers["Content-Type"] = "application/json";
    return headers;
  };

  handleUnauthorized = () => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem("admin_token");
      window.localStorage.removeItem("admin_user");
    } catch (e) {
      console.error(e);
    }
    window.location.reload();
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({ snackbar: { open: true, message, severity } });
  };

  closeSnackbar = () => {
    this.setState((s) => ({
      snackbar: { ...s.snackbar, open: false },
    }));
  };

  buildRunResultsQueryString = (pageIndex) => {
    const {
      tableRowsPerPage,
      orderStatusFilter,
      includeTestOrders,
      outcomeFilters,
    } = this.state;
    const classParts = [
      "verified",
      "suspicious",
      "excluded_test_order",
      "audit_error",
    ].filter((k) => outcomeFilters[k]);
    const params = new URLSearchParams();
    params.set("page", String(pageIndex + 1));
    params.set("limit", String(tableRowsPerPage));
    params.set("classifications", classParts.join(","));
    params.set("includeTestOrders", includeTestOrders ? "true" : "false");
    params.set("localStatus", orderStatusFilter);
    return params.toString();
  };

  loadRuns = async () => {
    const { runsPage, runsRowsPerPage } = this.state;
    this.setState({ runsLoading: true });
    try {
      const qs = new URLSearchParams({
        page: String(runsPage + 1),
        limit: String(runsRowsPerPage),
      });
      const res = await fetch(
        `${basePath}/api/v1/admin/creditguard-order-audits/runs?${qs.toString()}`,
        { headers: this.getAdminHeaders() }
      );
      const data = await res.json();
      if (res.status === 401) {
        this.handleUnauthorized();
        return;
      }
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בטעינת הרצות");
      }
      this.setState({
        runs: data.runs || [],
        runsTotal: typeof data.total === "number" ? data.total : 0,
      });
    } catch (e) {
      console.error(e);
      this.showSnackbar(e.message || "שגיאה בטעינת הרצות", "error");
    } finally {
      this.setState({ runsLoading: false });
    }
  };

  loadRunDetail = async (runId, { resetPage, pageIndex: pageIndexOpt } = {}) => {
    if (!runId) return;
    const requestSeq = this.runDetailRequestSeq + 1;
    this.runDetailRequestSeq = requestSeq;

    const pageIndex =
      pageIndexOpt != null
        ? pageIndexOpt
        : resetPage
        ? 0
        : this.state.tablePage;

    this.setState((s) => ({
      detailLoading: true,
      selectedRunId: runId,
      ...(resetPage ? { runDetail: null, tablePage: 0 } : {}),
    }));

    const qs = this.buildRunResultsQueryString(pageIndex);

    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/creditguard-order-audits/runs/${runId}?${qs}`,
        { headers: this.getAdminHeaders() }
      );
      const data = await res.json();
      if (res.status === 401) {
        this.handleUnauthorized();
        return;
      }
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בטעינת פירוט הרצה");
      }
      if (requestSeq !== this.runDetailRequestSeq) {
        return;
      }
      this.setState({
        runDetail: { run: data.run, results: data.results || [] },
        resultsTotal: typeof data.total === "number" ? data.total : 0,
        detailLoading: false,
      });
    } catch (e) {
      if (requestSeq !== this.runDetailRequestSeq) {
        return;
      }
      console.error(e);
      this.showSnackbar(e.message || "שגיאה בטעינת פירוט הרצה", "error");
      this.setState({ detailLoading: false, runDetail: null, resultsTotal: 0 });
    }
  };

  startNewRun = async () => {
    const { sinceInput, untilInput, limitInput } = this.state;
    const since = parseDateInput(sinceInput, false);
    const until = parseDateInput(untilInput, true);
    if (!since || !until) {
      this.showSnackbar("נא לבחור טווח תאריכים תקין", "error");
      return;
    }
    const limit = parseInt(limitInput, 10);
    if (!Number.isFinite(limit) || limit < 0) {
      this.showSnackbar("מגבלה לא תקינה", "error");
      return;
    }

    this.setState({ runSubmitting: true });
    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/creditguard-order-audits/runs`,
        {
          method: "POST",
          headers: this.getAdminHeaders(true),
          body: JSON.stringify({
            since: since.toISOString(),
            until: until.toISOString(),
            limit,
          }),
        }
      );
      const data = await res.json();
      if (res.status === 401) {
        this.handleUnauthorized();
        return;
      }
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בהרצת הביקורת");
      }
      this.showSnackbar("הביקורת הושלמה", "success");
      await this.loadRuns();
      if (data.run && data.run._id) {
        await this.loadRunDetail(data.run._id, { resetPage: true });
      }
    } catch (e) {
      console.error(e);
      this.showSnackbar(e.message || "שגיאה בהרצת הביקורת", "error");
    } finally {
      this.setState({ runSubmitting: false });
    }
  };

  openDetail = (row) => {
    this.setState({ detailOpen: true, detailResult: row });
  };

  closeDetail = () => {
    this.setState({ detailOpen: false, detailResult: null });
  };

  renderDetailDialog = () => {
    const { classes } = this.props;
    const { detailOpen, detailResult } = this.state;
    if (!detailResult) return null;

    const localOrder = getResultLocalView(detailResult);
    const liveFallback = rowUsesLiveLocalFallback(detailResult);

    const sourceKey = detailResult.source;
    const sourceLabel =
      labels.sourceLabels[sourceKey] || labels.sourceLabels.none;

    return (
      <Dialog
        open={detailOpen}
        onClose={this.closeDetail}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{labels.detail}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" className={classes.secondaryText}>
            מזהה הרצה: {detailResult.runId}
          </Typography>
          {liveFallback && (
            <Alert severity="warning" style={{ marginTop: 12, marginBottom: 8 }}>
              {labels.liveFallbackNotice}
            </Alert>
          )}
          <Box className={classes.detailGrid}>
            <Typography variant="body2">מספר הזמנה</Typography>
            <Typography variant="body2">{detailResult.orderId}</Typography>

            <Typography variant="body2">Mongo order id</Typography>
            <Typography variant="body2" style={{ wordBreak: "break-all" }}>
              {detailResult.orderMongoId || "—"}
            </Typography>

            <Typography variant="body2">מזהה עסקה (txId)</Typography>
            <Typography variant="body2" style={{ wordBreak: "break-all" }}>
              {detailResult.txId || "—"}
            </Typography>

            <Typography variant="body2">סטטוס מקומי</Typography>
            <Typography variant="body2">
              {localOrder.status || "—"}
            </Typography>

            <Typography variant="body2">אימייל לקוח</Typography>
            <Typography variant="body2">{localOrder.email || "—"}</Typography>

            <Typography variant="body2">סכום צפוי (מקומי)</Typography>
            <Typography variant="body2">
              {localOrder.expectedAmountAgorot != null
                ? `${formatAgorot(localOrder.expectedAmountAgorot)} ${
                    localOrder.expectedCurrency || ""
                  }`
                : "—"}
            </Typography>

            <Typography variant="body2">מקור סכום צפוי</Typography>
            <Typography variant="body2">
              {localOrder.expectedAmountSource === "ccResult"
                ? "ccResult"
                : localOrder.expectedAmountSource === "cart"
                ? `cart.total (${localOrder.cartTotal != null ? `₪${Number(
                    localOrder.cartTotal
                  ).toFixed(2)}` : "—"}, ${localOrder.payments || 1} תשלומים)`
                : "—"}
            </Typography>

            <Typography variant="body2">מקור אימות</Typography>
            <Typography variant="body2">{sourceLabel}</Typography>

            <Typography variant="body2">תוצאת ביקורת</Typography>
            <Box>
              <Chip
                size="small"
                label={detailResult.classification}
                color={classificationChipColor(detailResult.classification)}
              />
              {Array.isArray(detailResult.suspiciousReasons) &&
                detailResult.suspiciousReasons.length > 0 && (
                  <div className={classes.chipRow}>
                    {detailResult.suspiciousReasons.map((r) => (
                      <Chip key={r} size="small" variant="outlined" label={r} />
                    ))}
                  </div>
                )}
              {detailResult.errorReason && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{ marginTop: 8 }}
                >
                  {detailResult.errorReason}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDetail}>{labels.close}</Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      runs,
      runsLoading,
      runsPage,
      runsRowsPerPage,
      runsTotal,
      selectedRunId,
      runDetail,
      detailLoading,
      sinceInput,
      untilInput,
      limitInput,
      runSubmitting,
      orderStatusFilter,
      includeTestOrders,
      outcomeFilters,
      tablePage,
      tableRowsPerPage,
      resultsTotal,
      snackbar,
    } = this.state;

    const pageResults = runDetail?.results || [];
    const rangeStart =
      resultsTotal === 0 ? 0 : tablePage * tableRowsPerPage + 1;
    const rangeEnd = tablePage * tableRowsPerPage + pageResults.length;

    return (
      <div className={classes.root}>
        <Typography variant="h5" className={classes.sectionTitle}>
          {labels.title}
        </Typography>

        <Paper className={classes.section} elevation={0}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {labels.newRun}
          </Typography>
          <div className={classes.filterBar}>
            <TextField
              label={labels.since}
              type="date"
              value={sinceInput}
              onChange={(e) => this.setState({ sinceInput: e.target.value })}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
            />
            <TextField
              label={labels.until}
              type="date"
              value={untilInput}
              onChange={(e) => this.setState({ untilInput: e.target.value })}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
            />
            <TextField
              label={labels.limit}
              value={limitInput}
              onChange={(e) => this.setState({ limitInput: e.target.value })}
              variant="outlined"
              size="small"
              style={{ width: 160 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={
                runSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={this.startNewRun}
              disabled={runSubmitting}
            >
              {labels.startRun}
            </Button>
          </div>
          <Typography variant="caption" className={classes.secondaryText}>
            טווח התאריכים נשלח לשרת ומסנן הזמנות לפי createdAt. שורות שנוצרו
            בהרצה כוללות צילום מצב מקומי שנשמר בזמן ההרצה. שורות ישנות ללא
            צילום עשויות להציג נתונים מהזמנה חיה לצורכי עזר בלבד — אינם צילום
            היסטורי.
          </Typography>
        </Paper>

        <Paper className={classes.section} elevation={0}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {labels.history}
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={this.loadRuns}
              disabled={runsLoading}
            >
              {labels.reloadRuns}
            </Button>
          </div>
          {runsLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : runs.length === 0 ? (
            <Typography className={classes.secondaryText}>
              {labels.selectRun}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>התחלה</TableCell>
                    <TableCell>סיום</TableCell>
                    <TableCell>מגבלה</TableCell>
                    <TableCell>מעובד</TableCell>
                    <TableCell>מאומת</TableCell>
                    <TableCell>חשוד</TableCell>
                    <TableCell>שגיאות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((r) => {
                    const sel = selectedRunId === r._id;
                    return (
                      <TableRow
                        key={r._id}
                        className={`${classes.runRow} ${
                          sel ? classes.runRowSelected : ""
                        }`}
                        onClick={() =>
                          this.loadRunDetail(r._id, { resetPage: true })
                        }
                      >
                        <TableCell>{formatDateTime(r.startedAt)}</TableCell>
                        <TableCell>{formatDateTime(r.completedAt)}</TableCell>
                        <TableCell>{r.filter?.limit ?? "—"}</TableCell>
                        <TableCell>{r.stats?.processed ?? "—"}</TableCell>
                        <TableCell>{r.stats?.verified ?? "—"}</TableCell>
                        <TableCell>{r.stats?.suspicious ?? "—"}</TableCell>
                        <TableCell>{r.stats?.audit_error ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!runsLoading && runs.length > 0 && (
            <TablePagination
              component="div"
              count={runsTotal}
              page={runsPage}
              onPageChange={(e, p) =>
                this.setState({ runsPage: p }, () => this.loadRuns())
              }
              rowsPerPage={runsRowsPerPage}
              onRowsPerPageChange={(e) =>
                this.setState(
                  {
                    runsRowsPerPage: parseInt(e.target.value, 10),
                    runsPage: 0,
                  },
                  () => this.loadRuns()
                )
              }
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </Paper>

        <Paper className={classes.section} elevation={0}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {labels.filters}
          </Typography>
          <div className={classes.filterBar}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
              <InputLabel>{labels.orderStatus}</InputLabel>
              <Select
                label={labels.orderStatus}
                value={orderStatusFilter}
                onChange={(e) =>
                  this.setState(
                    { orderStatusFilter: e.target.value, tablePage: 0 },
                    () =>
                      this.state.selectedRunId &&
                      this.loadRunDetail(this.state.selectedRunId, {
                        resetPage: true,
                      })
                  )
                }
              >
                <MenuItem value="both">{labels.orderStatusBoth}</MenuItem>
                <MenuItem value="paid">{labels.orderStatusPaid}</MenuItem>
                <MenuItem value="closed">{labels.orderStatusClosed}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeTestOrders}
                  onChange={(e) =>
                    this.setState(
                      { includeTestOrders: e.target.checked, tablePage: 0 },
                      () =>
                        this.state.selectedRunId &&
                        this.loadRunDetail(this.state.selectedRunId, {
                          resetPage: true,
                        })
                    )
                  }
                  color="primary"
                />
              }
              label={labels.includeTest}
            />
            <FormGroup row style={{ marginLeft: 8 }}>
              <Typography
                variant="body2"
                style={{ alignSelf: "center", marginRight: 8 }}
              >
                {labels.outcomes}:
              </Typography>
              {[
                [OUTCOME_KEYS.verified, labels.outcomeVerified],
                [OUTCOME_KEYS.suspicious, labels.outcomeSuspicious],
                [OUTCOME_KEYS.excluded, labels.outcomeExcluded],
                [OUTCOME_KEYS.errors, labels.outcomeErrors],
              ].map(([key, lab]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={!!outcomeFilters[key]}
                      onChange={(e) =>
                        this.setState(
                          {
                            outcomeFilters: {
                              ...outcomeFilters,
                              [key]: e.target.checked,
                            },
                            tablePage: 0,
                          },
                          () =>
                            this.state.selectedRunId &&
                            this.loadRunDetail(this.state.selectedRunId, {
                              resetPage: true,
                            })
                        )
                      }
                      color="primary"
                      size="small"
                    />
                  }
                  label={lab}
                />
              ))}
            </FormGroup>
          </div>

          {detailLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : !runDetail ? (
            <Typography className={classes.secondaryText}>
              {labels.selectRun}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" className={classes.secondaryText}>
                מציג {rangeStart}–{rangeEnd} מתוך {resultsTotal} שורות (מסוננות
                בשרת)
              </Typography>
              <TableContainer style={{ marginTop: 16 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{labels.table.orderId}</TableCell>
                      <TableCell>{labels.table.txId}</TableCell>
                      <TableCell>{labels.table.classification}</TableCell>
                      <TableCell>{labels.table.source}</TableCell>
                      <TableCell align="right">{labels.table.actions}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageResults.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography className={classes.secondaryText}>
                            אין שורות להצגה
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pageResults.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell>
                            <Typography variant="body2">{row.orderId}</Typography>
                            <Typography
                              variant="caption"
                              className={classes.secondaryText}
                              display="block"
                            >
                              {getResultLocalView(row).status || "—"}
                              {rowUsesLiveLocalFallback(row)
                                ? " (חי)"
                                : ""}
                            </Typography>
                          </TableCell>
                          <TableCell
                            style={{ maxWidth: 200, wordBreak: "break-all" }}
                          >
                            {row.txId || "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.classification}
                              color={classificationChipColor(
                                row.classification
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            {labels.sourceLabels[row.source] ||
                              labels.sourceLabels.none}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => this.openDetail(row)}
                            >
                              פירוט
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={resultsTotal}
                page={tablePage}
                onPageChange={(e, p) =>
                  this.setState({ tablePage: p }, () => {
                    if (!this.state.selectedRunId) return;
                    this.loadRunDetail(this.state.selectedRunId, {
                      pageIndex: p,
                    });
                  })
                }
                rowsPerPage={tableRowsPerPage}
                onRowsPerPageChange={(e) =>
                  this.setState(
                    {
                      tableRowsPerPage: parseInt(e.target.value, 10),
                      tablePage: 0,
                    },
                    () => {
                      if (!this.state.selectedRunId) return;
                      this.loadRunDetail(this.state.selectedRunId, {
                        pageIndex: 0,
                      });
                    }
                  )
                }
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Paper>

        {this.renderDetailDialog()}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

CreditGuardOrderAuditManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreditGuardOrderAuditManager);
